import { Camera, Mesh, Plane, Program, Renderer, Texture, Transform, type OGLRenderingContext } from 'ogl';
import { useEffect, useRef, useState, useMemo, memo } from 'react';

// Inline cn utility to avoid SSR issues
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

/*
 * --------------------------------
 * Types
 * -----------------------------------
 */
export interface GalleryItem {
  image: string;
  text: string;
  cardBackgroundColor?: string;

  /**
   * Optional callback to draw custom content on the card canvas.
   * If provided, this replaces the default image loading.
   */
  onDraw?: (
    ctx: CanvasRenderingContext2D,
    info: { width: number; height: number; index: number },
  ) => void | Promise<void>;
}

interface CircularGalleryProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * An array of image and text objects for the gallery.
   */
  items?: GalleryItem[];

  /**
   * The amount of curvature. Higher values create a stronger bend.
   * @default 3
   */
  bend?: number;

  /**
   * The border radius for the images, as a percentage (0.0 to 0.5).
   * @default 0.05
   */
  borderRadius?: number;

  /**
   * Multiplier for scroll interaction speed.
   * @default 2
   */
  scrollSpeed?: number;

  /**
   * Easing factor for the scroll animation (lower is smoother).
   * @default 0.05
   */
  scrollEase?: number;

  /**
   * Optional class name to override the default font (e.g., from Next/font).
   */
  fontClassName?: string;

  /**
   * The reference height used to calculate the scale.
   * @default 1500
   */
  baseRefHeight?: number;

  /**
   * The base height of the gallery items.
   * @default 800
   */
  baseItemHeight?: number;

  /**
   * The base width of the gallery items.
   * @default 500
   */
  baseItemWidth?: number;

  /**
   * The padding between items.
   * @default 0.5
   */
  itemPadding?: number;

  /**
   * Custom vertex shader string.
   */
  vertexShader?: string;

  /**
   * Custom fragment shader string.
   */
  fragmentShader?: string;

  /**
   * Custom uniforms to pass to the shader.
   */
  uniforms?: Record<string, { value: any }>;
}

/*
 * --------------------------------
 * OGL Helper Utilities
 * -----------------------------------
 */
function debounce(func: (...args: any[]) => void, wait: number) {
  let timeout: NodeJS.Timeout;

  return function (this: any, ...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

function lerp(p1: number, p2: number, t: number) {
  return p1 + (p2 - p1) * t;
}

function autoBind(instance: object) {
  const proto = Object.getPrototypeOf(instance);
  Object.getOwnPropertyNames(proto).forEach((key) => {
    if (key !== 'constructor' && typeof (instance as any)[key] === 'function') {
      (instance as any)[key] = (instance as any)[key].bind(instance);
    }
  });
}

function createTextTexture(gl: OGLRenderingContext, text: string, font: string, color: string) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d')!;
  context.font = font;

  const metrics = context.measureText(text);
  const textWidth = Math.ceil(metrics.width);
  const textHeight = Math.ceil(parseInt(font, 10) * 1.2);
  canvas.width = textWidth + 20;
  canvas.height = textHeight + 20;
  context.font = font;
  context.fillStyle = color;
  context.textBaseline = 'middle';
  context.textAlign = 'center';
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillText(text, canvas.width / 2, canvas.height / 2);

  const texture = new Texture(gl, { generateMipmaps: false });
  texture.image = canvas;
  return { texture, width: canvas.width, height: canvas.height };
}

/*
 * --------------------------------
 * OGL Classes
 * -----------------------------------
 * -----------------------------------
 */
class Title {
  gl: OGLRenderingContext;
  plane: Mesh;
  renderer: Renderer;
  text: string;
  textColor: string;
  font: string;
  mesh!: Mesh;

  constructor({
    gl,
    plane,
    renderer,
    text,
    textColor,
    font,
  }: {
    gl: OGLRenderingContext;
    plane: Mesh;
    renderer: Renderer;
    text: string;
    textColor: string;
    font: string;
  }) {
    autoBind(this);
    this.gl = gl;
    this.plane = plane;
    this.renderer = renderer;
    this.text = text;
    this.textColor = textColor;
    this.font = font;
    this.createMesh();
  }

  createMesh() {
    const { texture, width, height } = createTextTexture(this.gl, this.text, this.font, this.textColor);
    const geometry = new Plane(this.gl);
    const program = new Program(this.gl, {
      vertex: `
        attribute vec3 position;
        attribute vec2 uv;
        uniform mat4 modelViewMatrix;
        uniform mat4 projectionMatrix;
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragment: `
        precision highp float;
        uniform sampler2D tMap;
        varying vec2 vUv;
        void main() {
          vec4 color = texture2D(tMap, vUv);
          if (color.a < 0.1) discard;
          gl_FragColor = color;
        }
      `,
      uniforms: { tMap: { value: texture } },
      transparent: true,
    });
    this.mesh = new Mesh(this.gl, { geometry, program });

    const aspect = width / height;
    const textHeight = this.plane.scale.y * 0.15;
    const textWidth = textHeight * aspect;
    this.mesh.scale.set(textWidth, textHeight, 1);
    this.mesh.position.y = -this.plane.scale.y * 0.5 - textHeight * 0.5 - 0.05;
    this.mesh.setParent(this.plane);
  }
}

class Media {
  gl: OGLRenderingContext;
  geometry: Plane;
  image: string;
  index: number;
  length: number;
  renderer: Renderer;
  scene: Transform;
  screen: { width: number; height: number };
  text: string;
  viewport: { width: number; height: number };
  bend: number;
  textColor: string;
  borderRadius: number;
  font: string;
  program!: Program;
  plane!: Mesh;
  title!: Title;
  extra: number = 0;
  widthTotal: number = 0;
  width: number = 0;
  x: number = 0;
  scale: number = 1;
  padding: number = 2;
  speed: number = 0;
  isBefore: boolean = false;
  isAfter: boolean = false;
  imageLoaded: boolean = false;
  baseRefHeight: number;
  baseItemHeight: number;
  baseItemWidth: number;
  itemPadding: number;
  vertexShader?: string;
  fragmentShader?: string;
  customUniforms?: Record<string, { value: any }>;

  onDraw?: (
    ctx: CanvasRenderingContext2D,
    info: { width: number; height: number; index: number },
  ) => void | Promise<void>;

  constructor({
    geometry,
    gl,
    image,
    index,
    length,
    renderer,
    scene,
    screen,
    text,
    viewport,
    bend,
    textColor,
    borderRadius = 0,
    font,
    baseRefHeight,
    baseItemHeight,
    baseItemWidth,
    itemPadding,
    vertexShader,
    fragmentShader,
    customUniforms,
    onDraw,
  }: {
    geometry: Plane;
    gl: OGLRenderingContext;
    image: string;
    index: number;
    length: number;
    renderer: Renderer;
    scene: Transform;
    screen: { width: number; height: number };
    text: string;
    viewport: { width: number; height: number };
    bend: number;
    textColor: string;
    borderRadius: number;
    font: string;
    baseRefHeight: number;
    baseItemHeight: number;
    baseItemWidth: number;
    itemPadding: number;
    vertexShader?: string;
    fragmentShader?: string;
    customUniforms?: Record<string, { value: any }>;
    onDraw?: (
      ctx: CanvasRenderingContext2D,
      info: { width: number; height: number; index: number },
    ) => void | Promise<void>;
  }) {
    this.geometry = geometry;
    this.gl = gl;
    this.image = image;
    this.index = index;
    this.length = length;
    this.renderer = renderer;
    this.scene = scene;
    this.screen = screen;
    this.text = text;
    this.viewport = viewport;
    this.bend = bend;
    this.textColor = textColor;
    this.borderRadius = borderRadius;
    this.font = font;
    this.baseRefHeight = baseRefHeight;
    this.baseItemHeight = baseItemHeight;
    this.baseItemWidth = baseItemWidth;
    this.itemPadding = itemPadding;
    this.vertexShader = vertexShader;
    this.fragmentShader = fragmentShader;
    this.customUniforms = customUniforms;
    this.onDraw = onDraw;
    this.createShader();
    this.createMesh();
    this.createTitle();
    this.onResize();
  }

  createShader() {
    const texture = new Texture(this.gl, {
      generateMipmaps: false,
      minFilter: this.gl.LINEAR,
      magFilter: this.gl.LINEAR,
    });
    this.program = new Program(this.gl, {
      depthTest: false,
      depthWrite: false,
      vertex:
        this.vertexShader ||
        `
        precision highp float;
        attribute vec3 position;
        attribute vec2 uv;
        uniform mat4 modelViewMatrix;
        uniform mat4 projectionMatrix;
        uniform float uTime;
        uniform float uSpeed;
        uniform float uAlpha;
        varying vec2 vUv;
        varying float vAlpha;
        void main() {
          vUv = uv;
          vAlpha = uAlpha;
          vec3 p = position;
          // * Add a slight z-axis movement to create depth
          // * This creates a subtle parallax effect as you scroll
          // * The frequency and amplitude can be adjusted for different effects
          // * Here, we're using a simple sine and cosine pattern
          // * You can experiment with different frequencies and amplitudes
          // * to create more dynamic or subtle effects
          // p.z = (sin(p.x * 4.0 + uTime) * 0.5 + cos(p.y * 2.0 + uTime) * 0.5) * (0.1 + uSpeed * 0.5);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        }
      `,
      fragment:
        this.fragmentShader ||
        `
        precision highp float;
        uniform vec2 uImageSizes;
        uniform vec2 uPlaneSizes;
        uniform sampler2D tMap;
        uniform float uBorderRadius;
        varying vec2 vUv;
        varying float vAlpha;
        
        float roundedBoxSDF(vec2 p, vec2 b, float r) {
          vec2 d = abs(p) - b;
          return length(max(d, vec2(0.0))) + min(max(d.x, d.y), 0.0) - r;
        }
        
        void main() {
          vec2 ratio = vec2(
            min((uPlaneSizes.x / uPlaneSizes.y) / (uImageSizes.x / uImageSizes.y), 1.0),
            min((uPlaneSizes.y / uPlaneSizes.x) / (uImageSizes.y / uImageSizes.x), 1.0)
          );
          vec2 uv = vec2(
            vUv.x * ratio.x + (1.0 - ratio.x) * 0.5,
            vUv.y * ratio.y + (1.0 - ratio.y) * 0.5
          );
          vec4 color = texture2D(tMap, uv);
          
          float d = roundedBoxSDF(vUv - 0.5, vec2(0.5 - uBorderRadius), uBorderRadius);
          
          // Smooth antialiasing for edges
          float edgeSmooth = 0.002;
          float alpha = 1.0 - smoothstep(-edgeSmooth, edgeSmooth, d);
          
          gl_FragColor = vec4(color.rgb, alpha * vAlpha);
        }
      `,
      uniforms: {
        tMap: { value: texture },
        uPlaneSizes: { value: [0, 0] },
        uImageSizes: { value: [0, 0] },
        uSpeed: { value: 0 },
        uTime: { value: 100 * Math.random() },
        uBorderRadius: { value: this.borderRadius },
        uAlpha: { value: 0 },
        ...this.customUniforms,
      },
      transparent: true,
    });

    if (this.onDraw) {
      // Custom rendering via Canvas 2D
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const canvas = document.createElement('canvas');
      canvas.width = this.baseItemWidth * dpr;
      canvas.height = this.baseItemHeight * dpr;

      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.scale(dpr, dpr);

        const result = this.onDraw(ctx, {
          width: this.baseItemWidth,
          height: this.baseItemHeight,
          index: this.index,
        });

        // Handle async or sync drawing
        Promise.resolve(result).then(() => {
          texture.image = canvas;
          this.program.uniforms.uImageSizes.value = [canvas.width, canvas.height];
          this.imageLoaded = true;
          this.program.uniforms.uAlpha.value = 1;

          if (this.plane) {
            this.plane.visible = true;
          }
        });
      }
    } else {
      // Default image loading
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = this.image;

      img.onload = () => {
        texture.image = img;
        this.program.uniforms.uImageSizes.value = [img.naturalWidth, img.naturalHeight];
        this.imageLoaded = true;
        this.program.uniforms.uAlpha.value = 1;

        // * Make plane visible once image is loaded
        if (this.plane) {
          this.plane.visible = true;
        }
      };

      // * Handle image load errors
      img.onerror = () => {
        console.warn(`Failed to load image: ${this.image}`);
        this.imageLoaded = false;
      };
    }
  }

  createMesh() {
    this.plane = new Mesh(this.gl, {
      geometry: this.geometry,
      program: this.program,
    });
    this.plane.setParent(this.scene);

    // * Start invisible until image loads
    this.plane.visible = false;
  }

  createTitle() {
    this.title = new Title({
      gl: this.gl,
      plane: this.plane,
      renderer: this.renderer,
      text: this.text,
      textColor: this.textColor,
      font: this.font,
    });
  }

  update(scroll: { current: number; last: number }, direction: 'left' | 'right') {
    const planeOffset = this.plane.scale.x / 2;
    const viewportOffset = this.viewport.width / 2;

    // Check wrapping conditions BEFORE updating position
    const testX = this.x - scroll.current - this.extra;
    const willBeBefore = testX + planeOffset < -viewportOffset - this.width;
    const willBeAfter = testX - planeOffset > viewportOffset + this.width;

    // Update extra smoothly to prevent flickering
    if (direction === 'right' && willBeBefore) {
      this.extra -= this.widthTotal;
    }

    if (direction === 'left' && willBeAfter) {
      this.extra += this.widthTotal;
    }

    // * Now set the position
    this.plane.position.x = this.x - scroll.current - this.extra;

    const x = this.plane.position.x;
    const H = this.viewport.width / 2;

    if (this.bend === 0) {
      this.plane.position.y = 0;
      this.plane.rotation.z = 0;
    } else {
      const bAbs = Math.abs(this.bend);
      const R = (H * H + bAbs * bAbs) / (2 * bAbs);
      const effectiveX = Math.min(Math.abs(x), H);
      const arc = R - Math.sqrt(R * R - effectiveX * effectiveX);

      if (this.bend > 0) {
        this.plane.position.y = -arc;
        this.plane.rotation.z = -Math.sign(x) * Math.asin(effectiveX / R);
      } else {
        this.plane.position.y = arc;
        this.plane.rotation.z = Math.sign(x) * Math.asin(effectiveX / R);
      }
    }

    this.speed = scroll.current - scroll.last;
    this.program.uniforms.uTime.value += 0.04;
    this.program.uniforms.uSpeed.value = this.speed;

    this.isBefore = this.plane.position.x + planeOffset < -viewportOffset;
    this.isAfter = this.plane.position.x - planeOffset > viewportOffset;
  }

  onResize({
    screen,
    viewport,
  }: {
    screen?: { width: number; height: number };
    viewport?: { width: number; height: number };
  } = {}) {
    if (screen) {
      this.screen = screen;
    }

    if (viewport) {
      this.viewport = viewport;

      if ((this.plane.program.uniforms as any).uViewportSizes) {
        (this.plane.program.uniforms as any).uViewportSizes.value = [this.viewport.width, this.viewport.height];
      }
    }

    this.scale = this.screen.height / this.baseRefHeight;
    this.plane.scale.y = (this.viewport.height * (this.baseItemHeight * this.scale)) / this.screen.height;
    this.plane.scale.x = (this.viewport.width * (this.baseItemWidth * this.scale)) / this.screen.width;
    this.program.uniforms.uPlaneSizes.value = [this.plane.scale.x, this.plane.scale.y];
    this.padding = this.itemPadding;
    this.width = this.plane.scale.x + this.padding;
    this.widthTotal = this.width * this.length;
    this.x = this.width * this.index;
  }
}

class App {
  container: HTMLElement;
  scrollSpeed: number;
  scroll: { ease: number; current: number; target: number; last: number; position?: number };
  onCheckDebounce: () => void;
  renderer!: Renderer;
  gl!: OGLRenderingContext;
  camera!: Camera;
  scene!: Transform;
  planeGeometry!: Plane;
  mediasImages!: GalleryItem[];
  medias!: Media[];
  isDown: boolean = false;
  start: number = 0;
  screen!: { width: number; height: number };
  viewport!: { width: number; height: number };
  raf!: number;
  boundOnResize!: () => void;
  boundOnWheel!: (e: Event) => void;
  boundOnTouchDown!: (e: MouseEvent | TouchEvent) => void;
  boundOnTouchMove!: (e: MouseEvent | TouchEvent) => void;
  boundOnTouchUp!: () => void;
  resizeObserver!: ResizeObserver;
  config: {
    baseRefHeight: number;
    baseItemHeight: number;
    baseItemWidth: number;
    itemPadding: number;
  };
  vertexShader?: string;
  fragmentShader?: string;
  uniforms?: Record<string, { value: any }>;

  constructor(
    container: HTMLElement,
    {
      items,
      bend,
      textColor,
      borderRadius,
      font,
      scrollSpeed,
      scrollEase,
      config,
      vertexShader,
      fragmentShader,
      uniforms,
    }: {
      items?: GalleryItem[];
      bend: number;
      textColor: string;
      borderRadius: number;
      font: string;
      scrollSpeed: number;
      scrollEase: number;
      config: {
        baseRefHeight: number;
        baseItemHeight: number;
        baseItemWidth: number;
        itemPadding: number;
      };
      vertexShader?: string;
      fragmentShader?: string;
      uniforms?: Record<string, { value: any }>;
    },
  ) {
    this.container = container;
    this.scrollSpeed = scrollSpeed;
    this.scroll = { ease: scrollEase, current: 0, target: 0, last: 0 };
    this.onCheckDebounce = debounce(this.onCheck.bind(this), 200);
    this.config = config;
    this.vertexShader = vertexShader;
    this.fragmentShader = fragmentShader;
    this.uniforms = uniforms;

    autoBind(this);

    this.createRenderer();
    this.createCamera();
    this.createScene();
    this.onResize();
    this.createGeometry();
    this.createMedias(items, bend, textColor, borderRadius, font);
    this.update();
    this.addEventListeners();

    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.container);
  }

  updateConfiguration({
    baseItemWidth,
    baseItemHeight,
    bend,
    borderRadius,
    textColor,
    font,
  }: {
    baseItemWidth?: number;
    baseItemHeight?: number;
    bend?: number;
    borderRadius?: number;
    textColor?: string;
    font?: string;
  }) {
    let needsResize = false;

    if (baseItemWidth !== undefined) {
      this.config.baseItemWidth = baseItemWidth;
      needsResize = true;
    }

    if (baseItemHeight !== undefined) {
      this.config.baseItemHeight = baseItemHeight;
      needsResize = true;
    }

    if (bend !== undefined) {
      this.medias.forEach((media) => (media.bend = bend));
      needsResize = true;
    }

    if (borderRadius !== undefined) {
      this.medias.forEach((media) => (media.borderRadius = borderRadius));
    }

    if (textColor !== undefined) {
      this.medias.forEach((media) => (media.textColor = textColor));
    }

    if (font !== undefined) {
      this.medias.forEach((media) => (media.font = font));
    }

    if (needsResize) {
      this.onResize();
      this.medias.forEach((media) => {
        media.baseItemWidth = this.config.baseItemWidth;
        media.baseItemHeight = this.config.baseItemHeight;
        media.onResize({ screen: this.screen, viewport: this.viewport });
      });
    }
  }

  createRenderer() {
    this.renderer = new Renderer({
      alpha: true,
      antialias: true,
      dpr: Math.min(window.devicePixelRatio || 1, 2),
    });
    this.gl = this.renderer.gl;
    this.gl.clearColor(0, 0, 0, 0);

    // Ensure canvas fills container
    const canvas = this.gl.canvas as HTMLCanvasElement;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';

    this.container.appendChild(canvas);
  }

  createCamera() {
    this.camera = new Camera(this.gl);
    this.camera.fov = 45;
    this.camera.position.z = 20;
  }

  createScene() {
    this.scene = new Transform();
  }

  createGeometry() {
    this.planeGeometry = new Plane(this.gl, {
      heightSegments: 50,
      widthSegments: 100,
    });
  }

  createMedias(items: GalleryItem[] | undefined, bend: number, textColor: string, borderRadius: number, font: string) {
    const defaultItems: GalleryItem[] = [
      { image: `https://picsum.photos/seed/1/800/600?grayscale`, text: 'Bridge' },
      {
        image: `https://picsum.photos/seed/2/800/600?grayscale`,
        text: 'Desk Setup',
      },
      {
        image: `https://picsum.photos/seed/3/800/600?grayscale`,
        text: 'Waterfall',
      },
    ];

    const galleryItems = items && items.length > 0 ? items : defaultItems;
    this.mediasImages = [...galleryItems, ...galleryItems]; // Duplicate for seamless loop
    this.medias = this.mediasImages.map((data, index) => {
      return new Media({
        geometry: this.planeGeometry,
        gl: this.gl,
        image: data.image,
        index,
        length: this.mediasImages.length,
        renderer: this.renderer,
        scene: this.scene,
        screen: this.screen,
        text: data.text,
        viewport: this.viewport,
        bend,
        textColor,
        borderRadius,
        font,
        baseRefHeight: this.config.baseRefHeight,
        baseItemHeight: this.config.baseItemHeight,
        baseItemWidth: this.config.baseItemWidth,
        itemPadding: this.config.itemPadding,
        vertexShader: this.vertexShader,
        fragmentShader: this.fragmentShader,
        customUniforms: this.uniforms,
        onDraw: data.onDraw,
      });
    });
  }

  onTouchDown(e: MouseEvent | TouchEvent) {
    this.isDown = true;
    this.scroll.position = this.scroll.current;
    this.start = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;

    const y = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;

    // Calculate visual bounds to restrict interaction to items only
    const visualItemHeight = this.config.baseItemHeight * (this.screen.height / this.config.baseRefHeight);
    const topBound = (this.screen.height - visualItemHeight) / 2;
    const bottomBound = (this.screen.height + visualItemHeight) / 2;

    if (y < topBound || y > bottomBound) {
      this.isDown = false;
    }
  }

  onTouchMove(e: MouseEvent | TouchEvent) {
    if (!('touches' in e)) {
      const y = (e as MouseEvent).clientY;
      const visualItemHeight = this.config.baseItemHeight * (this.screen.height / this.config.baseRefHeight);
      const topBound = (this.screen.height - visualItemHeight) / 2;
      const bottomBound = (this.screen.height + visualItemHeight) / 2;

      if (y >= topBound && y <= bottomBound) {
        this.container.style.cursor = this.isDown ? 'grabbing' : 'grab';
      } else {
        this.container.style.cursor = 'default';
      }
    }

    if (!this.isDown) {
      return;
    }

    const x = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const distance = (this.start - x) * (this.scrollSpeed * 0.025);
    this.scroll.target = (this.scroll as any).position + distance;
  }

  onTouchUp() {
    this.isDown = false;
    this.onCheck();
  }

  onWheel(e: Event) {
    const wheelEvent = e as WheelEvent;
    const delta = wheelEvent.deltaY || (wheelEvent as any).wheelDelta || (wheelEvent as any).detail;
    this.scroll.target += (delta > 0 ? this.scrollSpeed : -this.scrollSpeed) * 0.2;
    this.onCheckDebounce();
  }

  onCheck() {
    if (!this.medias || !this.medias[0]) {
      return;
    }

    const width = this.medias[0].width;
    const itemIndex = Math.round(Math.abs(this.scroll.target) / width);
    const item = width * itemIndex;
    this.scroll.target = this.scroll.target < 0 ? -item : item;
  }

  onResize() {
    this.screen = {
      width: this.container.clientWidth,
      height: this.container.clientHeight,
    };
    this.renderer.setSize(this.screen.width, this.screen.height);
    this.camera.perspective({
      aspect: this.screen.width / this.screen.height,
    });

    const fov = (this.camera.fov * Math.PI) / 180;
    const height = 2 * Math.tan(fov / 2) * this.camera.position.z;
    const width = height * this.camera.aspect;
    this.viewport = { width, height };

    if (this.medias) {
      this.medias.forEach((media) => media.onResize({ screen: this.screen, viewport: this.viewport }));
    }
  }

  update() {
    this.scroll.current = lerp(this.scroll.current, this.scroll.target, this.scroll.ease);

    const direction = this.scroll.current > this.scroll.last ? 'right' : 'left';

    if (this.medias) {
      this.medias.forEach((media) => media.update(this.scroll, direction));
    }

    // Only render if we have a valid GL context
    if (this.gl && !this.gl.isContextLost()) {
      this.renderer.render({ scene: this.scene, camera: this.camera });
    }

    this.scroll.last = this.scroll.current;
    this.raf = window.requestAnimationFrame(this.update);
  }

  addEventListeners() {
    this.boundOnResize = this.onResize;
    this.boundOnWheel = this.onWheel;
    this.boundOnTouchDown = this.onTouchDown;
    this.boundOnTouchMove = this.onTouchMove;
    this.boundOnTouchUp = this.onTouchUp;

    window.addEventListener('mousewheel', this.boundOnWheel);
    window.addEventListener('wheel', this.boundOnWheel);
    this.container.addEventListener('mousedown', this.boundOnTouchDown);
    window.addEventListener('mousemove', this.boundOnTouchMove);
    window.addEventListener('mouseup', this.boundOnTouchUp);
    this.container.addEventListener('touchstart', this.boundOnTouchDown);
    window.addEventListener('touchmove', this.boundOnTouchMove);
    window.addEventListener('touchend', this.boundOnTouchUp);
  }

  destroy() {
    window.cancelAnimationFrame(this.raf);
    this.resizeObserver.disconnect();
    window.removeEventListener('mousewheel', this.boundOnWheel);
    window.removeEventListener('wheel', this.boundOnWheel);
    this.container.removeEventListener('mousedown', this.boundOnTouchDown);
    window.removeEventListener('mousemove', this.boundOnTouchMove);
    window.removeEventListener('mouseup', this.boundOnTouchUp);
    this.container.removeEventListener('touchstart', this.boundOnTouchDown);
    window.removeEventListener('touchmove', this.boundOnTouchMove);
    window.removeEventListener('touchend', this.boundOnTouchUp);

    if (this.renderer && this.renderer.gl && this.renderer.gl.canvas.parentNode) {
      this.renderer.gl.canvas.parentNode.removeChild(this.renderer.gl.canvas);
    }
  }
}

/*
 * --------------------------------
 * React Component
 * -----------------------------------
 */
const CircularGalleryComponent = ({
  items,
  bend = 3,
  borderRadius = 0.05,
  scrollSpeed = 2,
  scrollEase = 0.05,
  baseRefHeight = 1500,
  baseItemHeight = 800,
  baseItemWidth = 500,
  itemPadding = 0.5,
  className,
  fontClassName,
  vertexShader,
  fragmentShader,
  uniforms,
  ...props
}: CircularGalleryProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isClient, setIsClient] = useState(false);
  const appRef = useRef<App | null>(null);
  const isInitializing = useRef(false);

  // Only run on client side
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Memoize the items to prevent unnecessary re-initialization
  const itemsKey = useMemo(() => {
    if (!items) {
      return 'default';
    }

    // Include onDraw in key calculation if possible, or assume unstable
    // Since functions can't be stringified easily, we might rely on length or other props
    // or the user should memoize items array
    return items.map((item) => `${item.image}:${item.text}`).join('|');
  }, [items]);

  useEffect(() => {
    if (!isClient || !containerRef.current || isInitializing.current) {
      return undefined;
    }

    // Prevent multiple initializations
    isInitializing.current = true;

    // Get computed styles for theme-adaptive text
    const computedStyle = getComputedStyle(containerRef.current);
    const computedColor = computedStyle.color || 'hsl(var(--foreground))';
    const computedFontWeight = computedStyle.fontWeight || 'bold';
    const computedFontSize = computedStyle.fontSize || '30px';
    const computedFontFamily = computedStyle.fontFamily;

    const computedFont = `${computedFontWeight} ${computedFontSize} ${computedFontFamily}`;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (!containerRef.current) {
        return;
      }

      // Destroy existing instance if any
      if (appRef.current) {
        appRef.current.updateConfiguration({
          baseItemWidth,
          baseItemHeight,
          bend,
          borderRadius,
          textColor: computedColor,
          font: computedFont,
        });
        return;
      }

      // Create new instance
      const app = new App(containerRef.current, {
        items,
        bend,
        textColor: computedColor,
        borderRadius,
        font: computedFont,
        scrollSpeed,
        scrollEase,
        config: {
          baseRefHeight,
          baseItemHeight,
          baseItemWidth,
          itemPadding,
        },
        vertexShader,
        fragmentShader,
        uniforms,
      });

      appRef.current = app;
      isInitializing.current = false;
    }, 50);

    return () => {
      clearTimeout(timer);

      if (appRef.current) {
        appRef.current.destroy();
        appRef.current = null;
      }

      isInitializing.current = false;
    };
  }, [
    isClient,
    itemsKey,
    bend,
    borderRadius,
    scrollSpeed,
    scrollEase,
    baseRefHeight,
    baseItemHeight,
    baseItemWidth,
    itemPadding,
    vertexShader,
    fragmentShader,
    uniforms,
  ]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'w-full h-full overflow-hidden',

        // Cursor managed in JS to avoid grabbing empty space
        // isClient && 'cursor-grab active:cursor-grabbing',

        // Apply theme-aware defaults for getComputedStyle to read
        'text-foreground font-bold text-[30px]',
        fontClassName,
        className,
      )}
      {...props}
    >
      {!isClient && (
        <div className="flex items-center justify-center w-full h-full z-9999">
          <div className="text-white">Loading gallery...</div>
        </div>
      )}
    </div>
  );
};

/*
 * Memoize component to prevent unnecessary re-renders
 * Custom comparison function to deeply compare items array
 */
const CircularGallery = memo(CircularGalleryComponent, (prevProps, nextProps) => {
  // Compare all props except items first
  if (
    prevProps.bend !== nextProps.bend ||
    prevProps.borderRadius !== nextProps.borderRadius ||
    prevProps.scrollSpeed !== nextProps.scrollSpeed ||
    prevProps.scrollEase !== nextProps.scrollEase ||
    prevProps.fontClassName !== nextProps.fontClassName ||
    prevProps.className !== nextProps.className ||
    prevProps.baseRefHeight !== nextProps.baseRefHeight ||
    prevProps.baseItemHeight !== nextProps.baseItemHeight ||
    prevProps.baseItemWidth !== nextProps.baseItemWidth ||
    prevProps.itemPadding !== nextProps.itemPadding ||
    prevProps.vertexShader !== nextProps.vertexShader ||
    prevProps.fragmentShader !== nextProps.fragmentShader ||
    prevProps.uniforms !== nextProps.uniforms
  ) {
    return false; // Props changed, re-render
  }

  // Deep compare items array
  if (!prevProps.items && !nextProps.items) {
    return true;
  }

  if (!prevProps.items || !nextProps.items) {
    return false;
  }

  if (prevProps.items.length !== nextProps.items.length) {
    return false;
  }

  for (let i = 0; i < prevProps.items.length; i++) {
    if (
      prevProps.items[i].image !== nextProps.items[i].image ||
      prevProps.items[i].text !== nextProps.items[i].text ||
      prevProps.items[i].onDraw !== nextProps.items[i].onDraw
    ) {
      return false; // Items changed, re-render
    }
  }

  return true; // All props are the same, skip re-render
});

CircularGallery.displayName = 'CircularGallery';

export { CircularGallery };
